<template>
  <v-main>
    <app-navigation />
    <v-sheet
      id="scrolling-techniques-7"
      class="ukpl-blue-card-container"
      height="100%"
    >
      <v-container fluid height="100%">

        <!-- Captain team management — shown only if user captains at least one team -->
        <template v-if="captainedTeams.length > 0">
          <div class="profile-heading-wrap">
            <h2 class="profile-heading">My Teams (Captain)</h2>
          </div>
          <v-card
            v-for="team in captainedTeams"
            :key="team.id"
            class="mb-6"
          >
            <v-card-title
              style="cursor:pointer"
              @click="team.expanded = !team.expanded"
            >
              <v-img
                v-if="getUrl(team.logo)"
                :src="getUrl(team.logo)"
                max-width="48"
                max-height="48"
                class="mr-3"
              />
              {{ team.name }}
              <v-spacer />
              <v-chip small color="blue darken-2" class="ml-2">
                {{ leagueName(team.leagueId) }}
              </v-chip>
              <v-btn icon small class="ml-2" @click.stop="team.expanded = !team.expanded">
                <v-icon>{{ team.expanded ? 'mdi-chevron-up' : 'mdi-pencil' }}</v-icon>
              </v-btn>
            </v-card-title>
            <v-expand-transition>
            <v-card-text v-show="team.expanded">
              <v-form :ref="'teamForm-' + team.id">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="team.editName"
                      label="Team Name"
                      :rules="[v => !!v || 'Team name is required']"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="team.editWebsite"
                      label="Website"
                      outlined
                      dense
                      placeholder="https://"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="team.editDescription"
                      label="Team Description"
                      outlined
                      dense
                      rows="3"
                      auto-grow
                    />
                  </v-col>
                </v-row>

                <!-- Logo upload -->
                <v-row align="center">
                  <v-col cols="12" sm="6">
                    <v-file-input
                      v-model="team.logoFile"
                      label="Team Logo (PNG recommended, square, max 1000×1000)"
                      accept="image/jpeg,image/png"
                      prepend-icon="mdi-shield-account"
                      outlined
                      dense
                      show-size
                      @change="file => onLogoSelected(file, team)"
                    />
                  </v-col>
                  <v-col v-if="team.logoPreview" cols="12" sm="6" class="text-center">
                    <v-img
                      :src="team.logoPreview"
                      max-width="100"
                      max-height="100"
                      class="mx-auto"
                      contain
                    />
                    <div v-if="team.logoError" class="red--text caption mt-1">{{ team.logoError }}</div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-btn
                      color="blue darken-2"
                      :loading="team.saving"
                      :disabled="team.saving"
                      @click="saveTeam(team)"
                    >
                      Save Changes
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            </v-expand-transition>
          </v-card>
          <v-divider class="mb-6" />
        </template>

        <!-- Transparency warning dialog -->
        <v-dialog v-model="transparencyWarning.show" max-width="450">
          <v-card>
            <v-card-title class="headline">Logo may not look great</v-card-title>
            <v-card-text>
              The image you selected does not appear to have a transparent background.
              Team logos look best with a transparent background on the site.
              Do you want to use this image anyway?
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="cancelTransparencyWarning">Cancel</v-btn>
              <v-btn color="blue darken-2" @click="confirmTransparencyWarning">Use Anyway</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- All teams table -->
        <div class="profile-heading-wrap">
          <h2 class="profile-heading">Select your division</h2>
        </div>
        <v-row justify="center" align="center" class="row">
          <v-col cols="12" sm="12" md="12" xs="12" class="text-center">
            <v-select
              v-model="select"
              :items="divisions"
              item-text="name"
              item-value="id"
              label="Division"
              return-object
              dense
              v-on:change="reloadTeams"
              class="ukpl-team-select"
            ></v-select>
          </v-col>
        </v-row>
        <v-row justify="center" align="center" class="row">
          <v-col cols="12" sm="12" md="12" xs="12" class="text-center">
            <v-data-table
              :headers="headers"
              :items="teams"
              :items-per-page="-1"
              :loading="teamsLoading"
              class="ukpl-teams-table"
              item-key="id"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>Teams</v-toolbar-title>
                </v-toolbar>
              </template>
              <template v-slot:item.logo="{ item }">
                <v-img :src="getUrl(item.logo)" max-width="40" />
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
    <v-snackbar
      :timeout="2000"
      v-model="messageon"
      color="red accent-4"
      rounded="pill"
      elevation="24"
    >
      {{ message }}
    </v-snackbar>
    <v-snackbar
      :timeout="2000"
      v-model="savesuccess"
      color="green accent-4"
      rounded="pill"
      elevation="24"
    >
      Details Saved.
    </v-snackbar>
  </v-main>
</template>

<script>
import UserService from "../services/user.service";
import UserDetails from "../model/userdetails";
import DivisionService from "../services/division.service";
import TeamService from "../services/team.service";
import AppNavigation from "../components/AppNavigation";

const LEAGUE_NAMES = { 1: "UKPL", 2: "ECPL" };
const MAX_LOGO_DIMENSION = 1000;

export default {
  name: "Team",
  components: {
    AppNavigation,
  },
  data: () => ({
    select: { id: "1", name: "Elite" },
    divisions: [],
    teams: [],
    headers: [
      { text: "#", align: "start", value: "id" },
      { text: "Team Name", value: "name" },
      { text: "Team Logo", value: "logo" },
    ],
    teamsLoading: true,
    savesuccess: false,
    messageon: false,
    message: "",
    userDetails: new UserDetails(),
    captainedTeams: [],
    transparencyWarning: {
      show: false,
      pendingTeam: null,
      pendingFile: null,
    },
  }),
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  methods: {
    leagueName(leagueId) {
      return LEAGUE_NAMES[leagueId] || `League ${leagueId}`;
    },
    getUrl(logo) {
      if (!logo) {
        logo = "default-team-logo.png";
      }
      return "https://assets.ukpl.co.uk/thumbs/images/" + logo;
    },

    // ── Logo validation ──────────────────────────────────────────────
    onLogoSelected(file, team) {
      if (!file) {
        team.logoPreview = null;
        team.logoError = null;
        return;
      }

      // Enforce max file size 4 MB
      if (file.size > 4194304) {
        team.logoError = "File is larger than 4 MB.";
        team.logoFile = null;
        team.logoPreview = null;
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        // Squareness check
        if (img.width !== img.height) {
          team.logoError = `Logo must be square (got ${img.width}×${img.height}).`;
          team.logoFile = null;
          team.logoPreview = null;
          URL.revokeObjectURL(objectUrl);
          return;
        }

        // Max 1000×1000
        if (img.width > MAX_LOGO_DIMENSION || img.height > MAX_LOGO_DIMENSION) {
          team.logoError = `Logo must be at most ${MAX_LOGO_DIMENSION}×${MAX_LOGO_DIMENSION}px (got ${img.width}×${img.height}).`;
          team.logoFile = null;
          team.logoPreview = null;
          URL.revokeObjectURL(objectUrl);
          return;
        }

        team.logoError = null;
        team.logoPreview = objectUrl;

        // Transparency check via canvas
        this.checkTransparency(img, file, team);
      };
      img.onerror = () => {
        team.logoError = "Could not read the image file.";
        team.logoFile = null;
        team.logoPreview = null;
      };
      img.src = objectUrl;
    },

    checkTransparency(img, file, team) {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

      let hasTransparentPixel = false;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
          hasTransparentPixel = true;
          break;
        }
      }

      if (!hasTransparentPixel) {
        // Show warning dialog
        this.transparencyWarning.show = true;
        this.transparencyWarning.pendingTeam = team;
        this.transparencyWarning.pendingFile = file;
        // Clear preview until confirmed
        team.logoPreview = URL.createObjectURL(file);
      }
    },

    cancelTransparencyWarning() {
      const team = this.transparencyWarning.pendingTeam;
      if (team) {
        team.logoFile = null;
        team.logoPreview = null;
      }
      this.transparencyWarning.show = false;
      this.transparencyWarning.pendingTeam = null;
      this.transparencyWarning.pendingFile = null;
    },

    confirmTransparencyWarning() {
      this.transparencyWarning.show = false;
      this.transparencyWarning.pendingTeam = null;
      this.transparencyWarning.pendingFile = null;
    },

    // ── Save team details ────────────────────────────────────────────
    async saveTeam(team) {
      team.saving = true;
      try {
        await TeamService.updateTeamDetails(team.id, {
          name: team.editName,
          website: team.editWebsite,
          description: team.editDescription,
        });

        if (team.logoFile) {
          await TeamService.uploadTeamLogo(team.id, team.logoFile);
          // Refresh logo preview
          team.logo = null; // will reload from server on next visit
        }

        team.name = team.editName;
        this.savesuccess = true;
      } catch (err) {
        this.message =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Something went wrong saving team details.";
        this.messageon = true;
      } finally {
        team.saving = false;
      }
    },

    reloadTeams(sel) {
      this.initializeTeams(sel.id);
    },

    initializeDiv() {
      DivisionService.getDivisions().then(
        (response) => {
          this.divisions = response.data;
          this.select = this.divisions[0];
        },
        (error) => {
          this.message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        }
      );
    },

    initializeTeams(divId) {
      this.teamsLoading = true;
      TeamService.getTeamsForDivision(divId).then(
        (response) => {
          this.teams = response.data;
        },
        (error) => {
          this.message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        }
      );
      this.teamsLoading = false;
    },

    loadCaptainedTeams() {
      UserService.getCaptainedTeams().then(
        (response) => {
          this.captainedTeams = (response.data || []).map((t) => ({
            ...t,
            editName: t.name,
            editWebsite: t.website || "",
            editDescription: t.description || "",
            logoFile: null,
            logoPreview: null,
            logoError: null,
            saving: false,
            expanded: false,
          }));
        },
        () => {
          // Non-captains will get an empty list or 403 — silently ignore
          this.captainedTeams = [];
        }
      );
    },
  },
  created() {
    this.initializeDiv();
    this.initializeTeams(this.select.id);
    this.loadCaptainedTeams();
  },
  updated() {
    this.teamsLoading = false;
  },
  mounted() {},
};
</script>
